import {
  Component,
  DestroyRef,
  EventEmitter,
  inject,
  Input,
  OnChanges,
  OnDestroy,
  Output,
  SimpleChanges,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { MatButtonModule } from '@angular/material/button';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { PaymentRequest } from '@stripe/stripe-js';
import { catchError, EMPTY, finalize, from, Observable, of, switchMap, take, tap } from 'rxjs';
import { CurrencyCode } from 'shared/src/services/api.service';
import { PaymentFlow, PaymentHandlerService } from 'shared/src/services/payment-handler.service';

export interface ExpressPayCharge {
  clientSecret: string;
  stripeCustomerId?: string;
}

/**
 * Device-wallet CTA for the paywalls: an Apple Pay / Google Pay button backed by Stripe's
 * PaymentRequest API, plus the "Continue with other payment options" link that takes the user
 * to the full checkout instead.
 *
 * The host owns the money: it supplies `createCharge` (a subscription or a one-time intent) and
 * decides what success means. Everything about wallet availability lives here: when no wallet is
 * usable the component renders a plain Continue button instead, which leads to the same place as
 * the "other payment options" link — so hosts never branch on availability themselves.
 */
@Component({
  selector: 'app-express-pay',
  imports: [MatButtonModule, MatProgressSpinnerModule],
  templateUrl: './express-pay.component.html',
  styleUrl: './express-pay.component.scss',
})
export class ExpressPayComponent implements OnChanges, OnDestroy {
  @Input({ required: true }) amount!: number;
  @Input({ required: true }) currency!: CurrencyCode;
  // Creates the charge to confirm with the wallet's payment method. Called once per wallet
  // confirmation, after the user authorizes it in the sheet.
  @Input({ required: true }) createCharge!: () => Observable<ExpressPayCharge>;

  // Line item shown in the browser's payment interface.
  @Input() totalLabel = 'Total';
  @Input() requestEmail = false;
  // Stripe account to charge on; omit to resolve via the bootstrap endpoint (funnel default).
  @Input() publishableKey: string | undefined;
  @Input() flow: PaymentFlow = 'subscription';
  @Input() insufficientFundsAction: (() => void) | undefined;
  // 'lg' matches the funnel/discount paywall CTAs; 'sm' matches the credits card.
  @Input() size: 'lg' | 'sm' = 'lg';
  // Text before the wallet glyph — the glyph and "Pay" always follow it, so this reads as
  // "<label>  Pay". Paywalls that charge on confirmation say so explicitly; the default
  // keeps the funnels' existing wording (and its translations).
  @Input() walletActionLabel = $localize`:@@continueWith:Continue with`;
  // Label for the button shown when no wallet is available here.
  @Input() continueButtonTitle = $localize`:@@continue:Continue`;
  // Shown under the spinner while a charge settles; omit for a bare spinner.
  @Input() processingLabel: string | undefined;
  @Input() spinnerDiameter = 30;

  // The wallet button was tapped, before the sheet opens — hosts track and freeze here.
  @Output() walletClicked = new EventEmitter<void>();
  // The wallet sheet handed us the payer's email (requestEmail, or billing details). Emitted
  // synchronously before createCharge is called, so a host can adopt it in time for the charge.
  @Output() emailCollected = new EventEmitter<string>();
  // The sheet was dismissed without paying.
  @Output() cancelled = new EventEmitter<void>();
  @Output() otherOptionsClicked = new EventEmitter<void>();
  @Output() processingChange = new EventEmitter<boolean>();
  @Output() succeeded = new EventEmitter<void>();
  // Confirmation failed. PaymentHandlerService already showed the payment-error modal, so
  // hosts should only track — showing another would stack dialogs.
  @Output() confirmFailed = new EventEmitter<unknown>();
  // createCharge() itself failed, so no wallet confirmation was ever attempted and no modal
  // has been shown — the host owns both.
  @Output() chargeFailed = new EventEmitter<unknown>();

  expressPayMethod: 'apple_pay' | 'google_pay' | null = null;
  isProcessing = false;

  // Intentionally not localized: these are brand names.
  readonly applePayAriaLabel = 'Pay with Apple Pay';
  readonly googlePayAriaLabel = 'Pay with Google Pay';

  private paymentRequest: PaymentRequest | null = null;
  private isSheetOpen = false;
  private isDestroyed = false;
  private readonly destroyRef = inject(DestroyRef);

  constructor(private paymentHandler: PaymentHandlerService) {}

  ngOnChanges(changes: SimpleChanges): void {
    // totalLabel too: two packs can share a price, and the sheet's line item must still name
    // the one being bought.
    if (changes['amount'] || changes['currency'] || changes['totalLabel']) {
      this.syncPaymentRequest();
    }
  }

  ngOnDestroy(): void {
    this.isDestroyed = true;

    if (this.paymentRequest) {
      try {
        this.paymentRequest.abort();
      } catch {
        // No sheet open — abort() throws instead of no-oping.
      }

      this.paymentRequest = null;
    }
  }

  /**
   * What the visible CTA does: open the wallet sheet if there is one, otherwise head for the
   * full checkout. Public so a host's own out-of-band CTA (the funnel paywall's sticky button)
   * can mirror it without duplicating the availability check.
   */
  activate(): void {
    if (this.expressPayMethod) {
      this.walletButtonClicked();
      return;
    }

    this.otherOptionsClicked.emit();
  }

  walletButtonClicked(): void {
    // isSheetOpen: a second show() while the sheet is already up throws.
    if (!this.paymentRequest || !this.expressPayMethod || this.isProcessing || this.isSheetOpen) {
      return;
    }

    this.isSheetOpen = true;
    this.walletClicked.emit();
    this.paymentRequest.show();
  }

  /**
   * Creates the wallet request on the first amount, then only rewrites its total. Rebuilding it
   * per amount would blank the button until canMakePayment() resolved again.
   */
  private syncPaymentRequest(): void {
    if (this.isDestroyed || !this.amount) return;

    if (this.paymentRequest) {
      this.paymentRequest.update({
        currency: (this.currency || 'USD').toLowerCase(),
        total: { label: this.totalLabel, amount: Math.round(this.amount * 100) },
      });
      return;
    }

    this.paymentHandler
      .createPaymentRequest(
        this.amount,
        this.totalLabel,
        this.currency,
        this.requestEmail,
        this.publishableKey
      )
      .pipe(
        take(1),
        tap(paymentRequest => {
          if (this.isDestroyed) return;

          this.paymentRequest = paymentRequest;
        }),
        switchMap(paymentRequest => {
          if (this.isDestroyed) return of(null);

          return this.checkCanMakePayment(paymentRequest);
        }),
        catchError(error => {
          // No wallet, no button — the host's own CTA carries the user to full checkout.
          console.error('Failed to create the wallet payment request:', error);
          return of(null);
        }),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe();
  }

  private checkCanMakePayment(paymentRequest: PaymentRequest) {
    return from(paymentRequest.canMakePayment()).pipe(
      tap(result => {
        if (this.isDestroyed) return;

        // The request may have been dropped while canMakePayment was in flight — never attach
        // listeners to one this component no longer holds.
        if (paymentRequest !== this.paymentRequest) return;

        // Prioritize Apple Pay over Google Pay
        if (result?.['applePay']) {
          this.expressPayMethod = 'apple_pay';
        } else if (result?.['googlePay']) {
          this.expressPayMethod = 'google_pay';
        }

        if (this.expressPayMethod) {
          this.attachListeners(paymentRequest);
        }
      })
    );
  }

  private attachListeners(paymentRequest: PaymentRequest): void {
    paymentRequest.on('cancel', () => {
      this.isSheetOpen = false;

      if (this.isDestroyed) return;

      this.cancelled.emit();
    });

    paymentRequest.on('paymentmethod', event => {
      this.isSheetOpen = false;

      // Guard against the component being destroyed while the native sheet was open.
      if (this.isDestroyed) {
        event.complete('fail');
        return;
      }

      // Before createCharge(): the host's charge thunk reads its email at call time, and an
      // anonymous buyer's only email is the one the wallet just collected.
      const payerEmail = event.payerEmail || event.paymentMethod?.billing_details?.email;

      if (payerEmail) {
        this.emailCollected.emit(payerEmail);
      }

      this.setProcessing(true);

      this.createCharge()
        .pipe(
          take(1),
          switchMap(charge => {
            // Check again before confirming — the sheet may have outlived the page.
            if (this.isDestroyed) {
              event.complete('fail');
              return EMPTY;
            }

            return this.paymentHandler.handleExpressPayment(
              charge.clientSecret,
              event.paymentMethod,
              this.insufficientFundsAction,
              this.publishableKey,
              this.flow
            );
          }),
          tap(result => {
            // complete() must run even after destroy or the sheet stays spinning.
            event.complete(result.success ? 'success' : 'fail');

            if (this.isDestroyed) return;

            if (result.success) {
              this.succeeded.emit();
            } else {
              this.confirmFailed.emit(result.error);
            }
          }),
          catchError(error => {
            // Only createCharge failures reach here.
            event.complete('fail');

            if (!this.isDestroyed) {
              this.chargeFailed.emit(error);
            }

            return of(null);
          }),
          finalize(() => {
            if (this.isDestroyed) return;

            this.setProcessing(false);
          })
        )
        .subscribe();
    });
  }

  private setProcessing(isProcessing: boolean): void {
    this.isProcessing = isProcessing;
    this.processingChange.emit(isProcessing);
  }
}
